// @flow
import { compareAsNumbers } from 'comparators';

import { ledgerAccountComparators } from './comparators';
import ledgerAccountCategoryOrderById from './ledgerAccountCategoryOrderById';
import ledgerAccountCategoryTypeById from './ledgerAccountCategoryTypeById';
import ledgerCategoryOrderingTypes from './ledgerCategoryOrderingTypes';
import type {
  LedgerAccount,
  LedgerAccountCategory,
  ReportCategorySummaries,
  ReportLedgerSummaries,
} from './types';

const createLedgerAccountCategoriesMap = ledgerAccountCategoryOrders => {
  const ledgerAccountCategoriesMap: Map<string, LedgerAccountCategory> = new Map();
  ledgerAccountCategoryOrders.forEach(
    ({ ledgerAccountCategory: { id, name }, ledgerCategoryOrderingType }) => {
      ledgerAccountCategoriesMap.set(id, {
        id,
        ledgerAccounts: [],
        ledgerAccountCategoryType: ledgerAccountCategoryTypeById[id],
        ledgerCategoryOrderingType,
        name,
        order: ledgerAccountCategoryOrderById[id],
      });
    },
  );
  return ledgerAccountCategoriesMap;
};

const filterByFinancialPackageNotesForFiscalYear = (fiscalYearTag, internalNotes) => {
  const fiscalYearTagRegExp = new RegExp(`${fiscalYearTag}\\b`, 'i');
  const removeFiscalYearTagRegExp = new RegExp(`${fiscalYearTag}\\b`, 'ig');

  return internalNotes
    .filter(({ body }) => fiscalYearTagRegExp.test(body))
    .map(internalNote => ({
      ...internalNote,
      body: internalNote.body.replace(removeFiscalYearTagRegExp, '').trim(),
    }));
};

const compileFinancialPackageNotesData = (
  closingBalanceSheetCategorySummaries: Array<ReportCategorySummaries>,
  closingBalanceSheetLedgerSummaries: Array<ReportLedgerSummaries>,
  hashtagFiscalYear: string,
  hashtagPeerReview: string,
  hashtagResolved: string,
  incomeStatementCategorySummaries: Array<ReportCategorySummaries>,
  incomeStatementLedgerSummaries: Array<ReportLedgerSummaries>,
  ledgerAccounts: Array<LedgerAccount>,
  ledgerAccountCategoryOrders: Array<any>,
) => {
  const ledgerAccountCategoriesMap = createLedgerAccountCategoriesMap(ledgerAccountCategoryOrders);

  const ledgerAccountsMap: Map<string, LedgerAccount> = new Map();
  ledgerAccounts.forEach(ledgerAccountData => {
    const { id, category, internalNotes } = ledgerAccountData;

    const ledgerAccountCategory = category && ledgerAccountCategoriesMap.get(category.id);
    const peerReviewTagRegExp = new RegExp(`${hashtagPeerReview}\\b`, 'i');
    const resolvedTagRegExp = new RegExp(`${hashtagResolved}\\b`, 'i');
    const ledgerAccount = {
      ...ledgerAccountData,
      dataEntries: [],
      internalNotes: filterByFinancialPackageNotesForFiscalYear(hashtagFiscalYear, internalNotes),
      isPeerReviewed: internalNotes.some(({ body }) => peerReviewTagRegExp.test(body)),
      isResolved: internalNotes.some(({ body }) => resolvedTagRegExp.test(body)),
      // Summary data is filled in below
      closingBalanceInCents: undefined,
      sumTotalInCents: undefined,
    };
    if (ledgerAccountCategory) {
      ledgerAccountCategory.ledgerAccounts.push(ledgerAccount);
    }
    ledgerAccountsMap.set(id, ledgerAccount);
  });

  const closingBalanceSheetLedgerSummariesList = closingBalanceSheetLedgerSummaries || [];
  const closingBalanceSheetLedgerCategorySummariesList = closingBalanceSheetCategorySummaries || [];

  closingBalanceSheetLedgerSummariesList.forEach(
    ({ ledgerAccount: { id: ledgerAccountId }, balanceInCents }) => {
      const ledgerAccount = ledgerAccountsMap.get(ledgerAccountId);
      if (!ledgerAccount) {
        return;
      }
      ledgerAccount.closingBalanceInCents = balanceInCents;
    },
  );

  const incomeStatementLedgerSummariesList = incomeStatementLedgerSummaries || [];
  const incomeStatementLedgerCategorySummariesList = incomeStatementCategorySummaries || [];

  incomeStatementLedgerSummariesList.forEach(
    ({ ledgerAccount: { id: ledgerAccountId }, sumTotalInCents }) => {
      const ledgerAccount = ledgerAccountsMap.get(ledgerAccountId);
      if (!ledgerAccount) {
        return;
      }
      ledgerAccount.sumTotalInCents = sumTotalInCents;
    },
  );

  // Sort the lists of ledgers with balances after adding them to their respective categories
  ledgerAccountCategoriesMap.forEach(ledgerAccountCategory => {
    const { ledgerAccounts: ledgerAccountsForCategory, ledgerCategoryOrderingType } =
      ledgerAccountCategory;

    const ledgerAccountComparator =
      ledgerAccountComparators[ledgerCategoryOrderingType || ledgerCategoryOrderingTypes.CUSTOM];
    const ledgerAccountsWithBalancesOrNotes = ledgerAccountsForCategory.filter(
      ({ closingBalanceInCents, internalNotes, sumTotalInCents }) => {
        const hasBalance = closingBalanceInCents || sumTotalInCents;
        const hasNotes = internalNotes.length;
        return !!hasBalance || !!hasNotes;
      },
    );

    // eslint-disable-next-line no-param-reassign
    ledgerAccountCategory.ledgerAccounts =
      ledgerAccountsWithBalancesOrNotes.sort(ledgerAccountComparator);
  });

  const sortedLedgerAccountCategories = Array.from(ledgerAccountCategoriesMap.values()).sort(
    (categoryA, categoryB) => compareAsNumbers(categoryA.order, categoryB.order),
  );

  const ledgerAccountCategoryTotalsMap: Map<string, ?number> = new Map();
  closingBalanceSheetLedgerCategorySummariesList.forEach(({ category, balanceInCents }) => {
    ledgerAccountCategoryTotalsMap.set(category.id, balanceInCents);
  });

  incomeStatementLedgerCategorySummariesList.forEach(({ category, sumTotalInCents }) => {
    ledgerAccountCategoryTotalsMap.set(category.id, sumTotalInCents);
  });

  return {
    ledgerAccountCategories: sortedLedgerAccountCategories,
    ledgerAccountCategoryTotalsMap,
  };
};
export default compileFinancialPackageNotesData;
